import type { JsonMetadata, Metadata, Nft, Option, Sft } from "@metaplex-foundation/js";
import {
  Metaplex,
  parseOriginalOrPrintEditionAccount,
  toMetadata,
  toMetadataAccount,
  toMint,
  toMintAccount,
  toNft,
  toNftEdition,
  toSft,
} from "@metaplex-foundation/js";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { asyncSettlePromises, isPublicKey } from "@stepfinance/common";
import type { NFT, SolanaFloorNFTData } from "@stepfinance/nft";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import type { Dispatch, SetStateAction } from "react";

import { getEdition, getMetadata } from "../../models/metaplex";
import type { MetaplexNFT, NFTWithTokenAccount } from "../../models/nfts/nfts";
import { isCompressedNFT, isNFTWithTokenAccount } from "../../models/nfts/nfts";
import type { SolanaFloorNFTEndpointResponse } from "../../models/solanafloor/types";
import { fetchJSONFromUrl } from "../utils";
import { convertNFTNameToCollectionName } from "./string-utils";
import { getCollectionName } from "./utils";

/**
 * @param connection
 * @param tokenAccounts associated token accounts to fetch nfts for
 * @returns array of NFTWithTokenAccount objects
 */
export const fetchNFTsFromTokenAccounts = async (connection: Connection, tokenAccounts: NormalizedTokenAccount[]) => {
  const nfts: NFTWithTokenAccount[] = [];
  const nftMintAddresses = tokenAccounts.map((acct) => new PublicKey(acct.info.mint));

  const nftFetches = await fetchNFTsFromMintList(connection, nftMintAddresses, true);

  tokenAccounts.forEach((acct, index) => {
    const nft = nftFetches[index];
    if (nft) {
      const owner = acct.info.owner;
      const amount = acct.tokenAmount.uiAmount;
      nfts.push({
        ...nft,
        owner,
        amount,
        associatedTokenAccount: acct,
      });
    }
  });
  return nfts;
};

/**
 * @param connection
 * @param nftMintAddresses pubkeys to fetch.
 * @param nullable filter or return null for mints without metadata.
 * @returns array of NFT objects.
 */
export const fetchNFTsFromMintList = async (
  connection: Connection,
  nftMintAddresses: PublicKey[],
  nullable?: boolean,
) => {
  const metaplex = new Metaplex(connection);

  const nfts: (NFT | null)[] = [];
  const collectionNftsMap: Map<string, MetaplexNFT> = new Map();

  const nftMetadatas = (await metaplex.nfts().findAllByMintList({ mints: nftMintAddresses })) as (Metadata | null)[];

  // Parse the collection nft mints so we can fetch those also.
  const collectionMints: PublicKey[] = [];
  const tempSet: Set<string> = new Set();
  nftMetadatas.forEach((data) => data?.collection?.address && tempSet.add(data.collection.address.toBase58()));
  tempSet.forEach((mint) => collectionMints.push(new PublicKey(mint)));

  const allMintAddresses = nftMintAddresses.concat(collectionMints);
  const collectionMetadataAddresses = collectionMints.map((mint) => getMetadata(mint));
  const editionAddresses = allMintAddresses.map((mint) => getEdition(mint));

  // Fetch accounts and SolanaFloor data for each nft in the chunk.
  const [mintInfos, collectionMetadataInfos, editionInfos] = await Promise.all([
    metaplex.rpc().getMultipleAccounts(allMintAddresses, "confirmed"),
    metaplex.rpc().getMultipleAccounts(collectionMetadataAddresses, "confirmed"),
    metaplex.rpc().getMultipleAccounts(editionAddresses, "confirmed"),
  ]);

  const mints = mintInfos.map((acct) => toMint(toMintAccount(acct)));
  const collectionMetadatas = collectionMetadataInfos.map((acct) =>
    acct.exists ? toMetadata(toMetadataAccount(acct)) : null,
  );
  const editionAccounts = editionInfos.map((acct) => parseOriginalOrPrintEditionAccount(acct));

  // Merge regular and collection nft metadata arrays.
  const metadatas = [...nftMetadatas, ...collectionMetadatas];

  // Build Metaplex object for each nft and enrich to create NFTWithToken.
  for (let i = 0; i < allMintAddresses.length; i++) {
    let metaplexNft: Nft | Sft | undefined;

    const mint = mints[i];
    const editionAccount = editionAccounts[i];
    let metadata = metadatas[i];
    if (!metadata) {
      nullable && nfts.push(null);
      continue;
    }
    // Load json data in a separate query so we can paginate it.
    metadata = { ...metadata, jsonLoaded: false, json: null };

    // Check if Metaplex Nft or Sft
    const isNft =
      editionAccount.exists && mint.mintAuthorityAddress && mint.mintAuthorityAddress.equals(editionAccount.publicKey);

    // Build corresponding object
    if (isNft) {
      const edition = toNftEdition(editionAccount);
      metaplexNft = toNft(metadata, mint, edition);
    } else {
      metaplexNft = toSft(metadata, mint);
    }

    // Push collection nfts to separate array.
    if (i >= nftMintAddresses.length) {
      collectionNftsMap.set(metaplexNft.address.toBase58(), metaplexNft);
      continue;
    }

    const collectionName = getCollectionName(metaplexNft);

    nfts.push({
      ...metaplexNft,
      owner: "",
      amount: 1,
      floorPrice: null,
      floorPriceUSD: null,
      collectionName: collectionName,
      sfLoaded: false,
      solanaFloorData: null,
    });
  }
  nfts.forEach((nft) => {
    // Check if has collection nft. Set collectionName and collectionNFT fields.
    if (nft && nft.collection) {
      const collectionAddressToFind = nft.collection.address.toBase58();
      const collectionNFT = collectionNftsMap.get(collectionAddressToFind);
      if (collectionNFT) {
        nft.collectionNFT = collectionNFT;
        nft.collectionName = collectionNFT.name;
      }
    }
  });
  return nfts;
};

export const fetchAndLoadNFTOffChainData = (
  nftsToFetch: NFT[],
  setUserNFTs: Dispatch<SetStateAction<Map<string, NFT>>>,
) => {
  nftsToFetch.forEach((nft) => {
    if (!nft.jsonLoaded) {
      fetchJSONMetadata(nft)
        .then(({ mint, json }) => {
          setUserNFTs((currentState) => {
            const nftMap = new Map(currentState);
            const contextNFT = currentState.get(mint);
            if (!contextNFT) {
              return nftMap;
            }
            const existingName = contextNFT.name;
            const newName = existingName ? existingName : json?.name ?? existingName;
            const existingCollection = contextNFT.collectionName;
            const newCollectionName = existingCollection
              ? contextNFT.collectionName
              : convertNFTNameToCollectionName(json?.name) ?? "";
            nftMap.set(mint, {
              ...contextNFT,
              name: newName,
              collectionName: newCollectionName,
              json: json,
              jsonLoaded: true,
            });
            return nftMap;
          });
        })
        .catch((err) => {
          const mint = nft.address.toBase58();
          console.error(`${err} json metadata for ${mint}`);
        });
    }
  });
  const hasUnloadedSFData = nftsToFetch.some((nft) => !isCompressedNFT(nft) && !nft.sfLoaded);
  if (hasUnloadedSFData) {
    fetchNFTSolanaFloorDataMap(nftsToFetch)
      .then((res) => {
        setUserNFTs((currentState) => {
          const nftMap = new Map(currentState);
          res.forEach((data, mint) => {
            const contextNFT = currentState.get(mint);
            if (!contextNFT) {
              return;
            }
            nftMap.set(mint, {
              ...contextNFT,
              solanaFloorData: data,
              sfLoaded: true,
            });
          });
          return nftMap;
        });
      })
      .catch((err) => {
        console.error(`${err} SolanaFloor data for nfts on page`);
      });
  }
};

/**
 *
 * @param nfts nfts to fetch json metadata for.
 * @returns JsonMetadata objects mapped to corresponding mint addresses.
 */
export const fetchNFTJSONMetadataMap = async (nfts: NFT[]) => {
  const mappedJsons: Map<string, Option<JsonMetadata>> = new Map();
  const jsonFetchesMap = nfts
    .map((nft) => {
      const mintAddress = nft.address.toBase58();
      if (!nft.jsonLoaded && isNFTWithTokenAccount(nft)) {
        const metadataFetch: Promise<JsonMetadata> = fetchJSONFromUrl(nft.uri, { signal: AbortSignal.timeout(10000) });
        return [mintAddress, metadataFetch];
      }
    })
    .filter((x): x is (string | Promise<JsonMetadata>)[] => !!x);

  const loadedFetches = await asyncSettlePromises(
    jsonFetchesMap.map((item) => item[1] as Promise<JsonMetadata>),
    true,
  );

  loadedFetches.forEach((json, index) => {
    const mintAddress = jsonFetchesMap[index][0] as string;
    mappedJsons.set(mintAddress, json ?? null);
  });
  return mappedJsons;
};

export const fetchJSONMetadata = async (nft: NFT) => {
  const mint = nft.address.toBase58();
  const json = await fetchJSONFromUrl(nft.uri, { signal: AbortSignal.timeout(20000) });
  return { mint, json: json ? (json as JsonMetadata) : null };
};

/**
 *
 * @param nfts nfts to fetch SolanaFloor data for.
 * @returns SolanaFloorNFTData objects mapped to corresponding mint addresses.
 */
export const fetchNFTSolanaFloorDataMap = async (nfts: NFT[]) => {
  const mappedSfData: Map<string, Option<SolanaFloorNFTData>> = new Map();
  const mintAddresses = nfts
    .map((nft) => {
      if (!nft.sfLoaded) {
        return nft.address.toBase58();
      }
    })
    .filter((x): x is string => !!x);

  const solanaFloorResponse = await fetchNFTsByMintSolanaFloor(mintAddresses, true);

  mintAddresses.forEach((mint) => {
    const res = solanaFloorResponse.find((res) => res?.onchain?.mint?.trim() === mint);
    const data = res?.solanafloor ?? null;
    mappedSfData.set(mint, data);
  });
  return mappedSfData;
};

export const fetchNFTsByMintSolanaFloor = async (
  mints: string | PublicKey | string[] | PublicKey[],
  fetchCollectionData = false,
) => {
  const sfDataQueryParam = fetchCollectionData ? 1 : 0;

  if (Array.isArray(mints)) {
    const body = JSON.stringify(isPublicKey(mints[0]) ? mints.map((x) => x.toBase58()) : mints);
    const data: SolanaFloorNFTEndpointResponse[] = await fetchJSONFromUrl(
      `https://api.solanafloor.com/mint?sf_data=${sfDataQueryParam}`,
      {
        method: "POST",
        body: body,
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return data;
  } else {
    const mint = isPublicKey(mints) ? mints.toBase58() : mints;
    const data: SolanaFloorNFTEndpointResponse = await fetchJSONFromUrl(
      `https://api.solanafloor.com/mint/${mint}?sf_data=${sfDataQueryParam}`,
    );
    return [data];
  }
};
